import {Pupil} from "./Pupil.model";
import {PupilActivity} from "./PupilActivity.model";
import {File} from "./File.model";
import {Model} from "@/stores/AbstractBase.store";
import { User } from "./User.model";
import { Comparable } from "@/interfaces/Comparable.interface.ts";
import { Lesson } from "./Lesson.model";
import { LessonItem } from "./LessonItem.model";
import type { Goal } from "./Goal.model";

export class Observation implements Model, Comparable {

	public id ?: string;
	public note ?: string;
	public files : File[] = [];
	public classroomId ?: string;
	public classroomName ?: string;
	public pupil: Pupil;
	public date: Date | undefined;
	public author?: User;

	public fileContent ?: string;
	public fileName ?: string;

	public lesson ?: Lesson;
	public lessonItem ?: LessonItem;
	public pupilActivity ?: PupilActivity;


	static createFromApiData(data: any, pupil: Pupil | null = null) {
		if (!pupil) {
			pupil = Pupil.mapFromServer(data.pupil);
		}

		return (new Observation(pupil)).hydrateWithApiData(data);
	}

	constructor(pupil: Pupil) {
		this.pupil = pupil;
	}

	removeFile(file: File) {
		this.files = this.files.filter((f: File) => f.id !== file.id);
	}

	hydrateWithApiData(data: any): Observation {
		this.id = data.id;
		this.date = new Date(data.date);
		this.note = data.note;

		if (data.pupilActivity) {
			this.pupilActivity = PupilActivity.mapFromServer(data.pupilActivity);
		}

		if (data.lesson) {
			this.lesson = Lesson.mapFromServer(data.lesson);
		}

		if (data.lessonItem) {
			this.lessonItem = LessonItem.mapFromServer(data.lessonItem);
			if (this.lesson) {
				this.lessonItem.lesson = this.lesson;
			}
		}

		if (data.files) {
			this.files = data.files.map((fileData: any) => File.mapFromServer(fileData));
		}

		if (data.classroom) {
			this.classroomId = data.classroom.id;
			this.classroomName = data.classroom.name;
		}

		if (data.author) {
			this.author = User.createFromApiData(data.author);
		}

		return this;
	}

	asApiDataObject() {
		const serverData: any = {
			note: this.note,
			pupil: {
				id: this.pupil.id
			}
		}

		if (this.pupilActivity) {
			serverData.pupilActivity = {
				id: this.pupilActivity.id
			};
		}

		if (this.lesson) {
			serverData.lesson = {
				id: this.lesson.id
			};
		}

		if (this.lessonItem) {
			serverData.lessonItem = {
				id: this.lessonItem.id
			};
		}

		if (this.classroomId) {
			serverData.classroom = {
				id: this.classroomId
			};
		}

		// List of all existing images
		if (this.files) {
			serverData.files = this.files.map((file: File) => {
				return {
					id: file.id
				}
			});
		}

		// Old way to upload images
		if (this.fileContent && this.fileName) {
			serverData.fileContent  = this.fileContent;
			serverData.fileName = this.fileName;
		}

		return serverData;
	}

	clone(): Observation {
		let clonedInstance = Object.assign(
			Object.create(
				Object.getPrototypeOf(this)
			),
			this
		);
		delete clonedInstance.fileContent;
		delete clonedInstance.fileName;
		return clonedInstance;
	}

	setFromModel(observation: Observation) {
		this.id = observation.id;
		this.note = observation.note;
		this.pupil = observation.pupil;
		this.date = observation.date;
		this.files = [ ... observation.files ];
		this.classroomId = observation.classroomId;
		this.classroomName = observation.classroomName;

		this.lesson = observation.lesson;
		this.lessonItem = observation.lessonItem;
		this.pupilActivity = observation.pupilActivity;

		return this;
	}

	get imageFiles() {
		return this.files.filter((file: File) => file.isImage());
	}

	/**
	 * Check if observation contains enough information to be saved.
	 * @returns
	 */
	isValid(): boolean {
		return (!!this.note) || this.files.length > 0;
	}

	public equals(other: Model): boolean {
		if (!(other instanceof Observation)) {
			return false;
		}

		return JSON.stringify(this.asApiDataObject()) === JSON.stringify(other.asApiDataObject());
	}

	/**
	 * Get the most specific related activity
	 */
	get relatedActivity(): PupilActivity | LessonItem | Lesson | null {

		if (this.pupilActivity) {
			return this.pupilActivity;
		}

		if (this.lessonItem) {
			return this.lessonItem;
		}

		if (this.lesson) {
			return this.lesson;
		}

		return null;
	}

	/**
	 * Every goal on the observed lesson / lesson item / pupil activity, whatever curriculum it
	 * comes from. Callers that show or evaluate goals must scope this to the school's
	 * curriculum first (see `Organisations.store.curriculumGoals`).
	 */
	get relatedGoals(): Goal[] {

		const activity = this.relatedActivity;

		if (activity instanceof PupilActivity) {
			return activity.lessonItem?.lessonPlan?.goals ?? [];
		}

		if (activity instanceof LessonItem) {
			return activity.lessonPlan?.goals ?? [];
		}

		if (activity instanceof Lesson) {
			return activity.lessonItems.flatMap((lessonItem: LessonItem) => lessonItem.lessonPlan?.goals ?? []);
		}

		return [];
	}

	set relatedActivity(activity: PupilActivity | LessonItem | Lesson | null) {

		if (activity instanceof PupilActivity) {
			this.pupilActivity = activity;
			this.lessonItem = activity?.lessonItem;
			this.lesson = activity?.lessonItem?.lesson;
		} else if (activity instanceof LessonItem) {
			this.lessonItem = activity;
			this.lesson = activity.lesson;
			this.pupilActivity = undefined;
		} else if (activity instanceof Lesson) {
			this.lesson = activity;
			this.lessonItem = undefined;
			this.pupilActivity = undefined;
		} else {
			this.pupilActivity = undefined;
			this.lessonItem = undefined;
			this.lesson = undefined;
		}
	}
}
